"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Quetra = void 0;
var vpt_abr_network_estimator_1 = require("@sky-uk-ott/vpt-abr-network-estimator");
var abr_types_1 = require("../../abr-types");
var factorial_1 = require("../../utils/factorial");
var defaultLowBufferRatio = 90 / 240; // low reservoir ratio from BBA
var QUANTIZED_RHO_MIN = 0.5;
var QUANTIZED_RHO_MAX = 1.2;
var Quetra = /** @class */ (function () {
    function Quetra(logger, bandwidthEstimator, bufferAssessment, customConfig, playerConfig) {
        var _a;
        this.bandwidthEstimator = bandwidthEstimator;
        this.bufferAssessment = bufferAssessment;
        this.shouldWaitForBandwidth = true;
        this.tracksAscending = [];
        // Buffer Slack is a predictor of how much spare buffer space we expect to have
        this.quantizedBufferSlack = new Map();
        this.logger = logger.withContext('QUETRA');
        this.logger.verbose('Received config: ', customConfig);
        var reservoirCapacity = customConfig.reservoirCapacity;
        if (reservoirCapacity === undefined) {
            var lowBufferRatio = (_a = customConfig.reservoirRatio) !== null && _a !== void 0 ? _a : defaultLowBufferRatio;
            reservoirCapacity = playerConfig.bufferCapacity * lowBufferRatio;
        }
        this.config = __assign(__assign({ reservoirCapacity: reservoirCapacity, smoother: {
                type: vpt_abr_network_estimator_1.SmootherType.HARMONIC_MEAN,
            } }, customConfig), { bufferCapacity: playerConfig.bufferCapacity });
        this.logger.info('Using config: ', this.config);
        this.smoother = this.bandwidthEstimator.getSmoother(this.config.smoother);
        this.preloadQuantizedBufferSlack(this.config.bufferCapacity);
    }
    Object.defineProperty(Quetra.prototype, "name", {
        get: function () {
            return abr_types_1.AbrType.QUETRA;
        },
        enumerable: false,
        configurable: true
    });
    Quetra.prototype.setTracksAscending = function (tracksAscending) {
        this.tracksAscending = tracksAscending;
    };
    Quetra.prototype.notifiedPlaybackRateChange = function () {
        // The QUETRA paper does provide formulas on what should be done if the
        // playback rate is not 1, but they have assumed the playback rate is
        // equal to 1 to simplify a number of calculations. The "preloaded quantized buffer slacks"
        // are only valid for a playback rate of 1, so if we need to take into account
        // multiple playback rates we would have to do more mathematical calculations
        // in real-time. We would need to understand the impact of this (although it's
        // unlikely to be hugely significant)
    };
    Quetra.prototype.getRecommendedTracks = function () {
        if (!this.tracksAscending.length) {
            return null;
        }
        return this.chooseTracks();
    };
    Quetra.prototype.destroy = function () {
        this.smoother.unregister();
    };
    Quetra.prototype.chooseTracks = function () {
        var currentBufferLevel = this.bufferAssessment.getStabilisedBufferAhead();
        var _a = this.config, bufferCapacity = _a.bufferCapacity, reservoirCapacity = _a.reservoirCapacity;
        // Same lower reservoir maintenance as BBA
        // QUETRA's low buffer handling was pulled directly from
        // the BBA implementation. The ratio comes from a buffer
        // capacity of 240s and a reservoir of 90s. They apply this
        // ratio to every buffer capacity so the reservoir kicks in
        // at about 1/3 buffer occupancy.
        if (currentBufferLevel < reservoirCapacity) {
            return this.tracksAscending[0];
        }
        var bandwidthEstimate = this.smoother.getValue();
        var tracksWithBufferSlack = this.getBufferSlacks(this.tracksAscending, bandwidthEstimate, bufferCapacity);
        // If the lowest bitrate track has a buffer slack equal to the buffer capacity
        // it means we don't have the bandwidth to play any of them, so pick the lowest
        if (tracksWithBufferSlack[0].bufferSlack === bufferCapacity) {
            return tracksWithBufferSlack[0];
        }
        var minDiff = Infinity;
        var selectedTrack = tracksWithBufferSlack[0];
        for (var _i = 0, tracksWithBufferSlack_1 = tracksWithBufferSlack; _i < tracksWithBufferSlack_1.length; _i++) {
            var track = tracksWithBufferSlack_1[_i];
            var diff = Math.abs(track.bufferSlack - currentBufferLevel);
            if (diff <= minDiff) {
                minDiff = diff;
                selectedTrack = track;
            }
        }
        return selectedTrack;
    };
    Quetra.prototype.calculateXi = function (i, rho) {
        var xi = 0;
        for (var j = 0; j <= i; j++) {
            xi += (Math.pow(-1, j) / (0, factorial_1.factorial)(j, this.logger)) * Math.pow(i - j, j) * Math.pow(rho, j) * Math.exp((i - j) * rho);
        }
        return xi;
    };
    Quetra.prototype.calculateBufferSlack = function (bufferCapacity, rho) {
        var sumXi = 0;
        for (var i = 0; i <= bufferCapacity - 1; i++) {
            sumXi += this.calculateXi(i, rho);
        }
        return sumXi / (1 + rho * this.calculateXi(bufferCapacity - 1, rho));
    };
    Quetra.prototype.preloadQuantizedBufferSlack = function (bufferCapacity) {
        // Decimal addition in JS is messy, we can use toFixed to mitigate rounding errors
        function checkIsLessThanMax(rho) {
            var roundedRho = parseFloat(rho.toFixed(2));
            return roundedRho <= QUANTIZED_RHO_MAX;
        }
        for (var rho = QUANTIZED_RHO_MIN; checkIsLessThanMax(rho); rho += 0.01) {
            var roundedRho = parseFloat(rho.toFixed(2));
            var bufferSlack = this.calculateBufferSlack(bufferCapacity, roundedRho);
            this.quantizedBufferSlack.set(roundedRho, bufferSlack);
        }
    };
    Quetra.prototype.getBufferSlacks = function (tracks, bandwidthEstimate, bufferCapacity) {
        var _this = this;
        return tracks.map(function (track) {
            // Rho will be >1 if we have lots of bandwidth, or <1 if we do not
            // have enough bandwidth
            var rho = bandwidthEstimate / track.combinedBandwidth;
            var roundedRho = parseFloat(rho.toFixed(2));
            var bufferSlack;
            // The research paper sets these values the opposite way round
            // but the code implementation uses this solution which appears
            // to be correct based on rho and buffer slack intention:
            //
            // If rho is low, we do not have enough bandwidth, therefore we have
            // a fully depleted buffer and our buffer slack is equal to the buffer
            // capacity.
            //
            // If rho is high, we have enough bandwidth, therefore we fill the
            // buffer with downloaded segments and our buffer slack is 0.
            if (roundedRho < QUANTIZED_RHO_MIN) {
                bufferSlack = bufferCapacity;
            }
            else if (roundedRho > QUANTIZED_RHO_MAX) {
                bufferSlack = 0;
            }
            else {
                var quantizedBufferSlack = _this.quantizedBufferSlack.get(roundedRho);
                if (quantizedBufferSlack === undefined) {
                    // Should never get here
                    _this.logger.error("Tried to retrieve a quantized buffer slack for a key which did not exist (key=".concat(roundedRho, ")"));
                    bufferSlack = 0;
                }
                else {
                    bufferSlack = quantizedBufferSlack;
                }
            }
            return __assign(__assign({}, track), { bufferSlack: bufferSlack });
        });
    };
    return Quetra;
}());
exports.Quetra = Quetra;
